/**
 * InvSitesPage — Gestion des sites inventaire depuis les succursales SOFI
 * Accessible : /sites-inventaire dans GS-Admin
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, useToast } from '@gs/ui';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';
import { ToggleLeft, ToggleRight, Building2, User } from 'lucide-react';

interface Employee {
  id: string;
  succursale_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
}

interface InvSite {
  id: string;
  codeSofi: string;
  name: string;
  ville: string | null;
  nomSofi: string | null;
  isActive: boolean;
  updatedAt: string;
  // NULLABLE depuis la mig 172 : NULL = « aucun gestionnaire connu ». Avant, le
  // serveur renvoyait 'admin@groupesignalisation.ca' — l'ecran affichait donc une
  // adresse joignable qui ne designe personne.
  managerName: string | null;
  managerEmail: string | null;
  managerEmployeeId: string | null;
  succursaleId: string | null;
}

type InvSitesPayload = { sites: InvSite[]; employees: Employee[] };

export default function InvSitesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['inv-sites'],
    queryFn: () => api.get<InvSitesPayload>('/api/inv-sites'),
    enabled: !!user,
  });
  const sites = data?.sites ?? [];
  const employees = data?.employees ?? [];

  const toggle = useMutation({
    mutationFn: (id: string) =>
      api.patch<{ id: string; isActive: boolean }>(`/api/inv-sites/${id}/toggle`, {}),
    onSuccess: (result) => {
      queryClient.setQueryData<InvSitesPayload>(['inv-sites'], (old) => {
        if (!old) return old;
        return {
          ...old,
          sites: old.sites.map((s) =>
            s.id === result.id ? { ...s, isActive: result.isActive } : s,
          ),
        };
      });
      toast(
        result.isActive ? 'Site activé dans GS-Inventaire' : 'Site masqué dans GS-Inventaire',
        result.isActive ? 'success' : 'warning',
      );
    },
    onError: () => toast('Erreur lors du changement de statut', 'error'),
  });

  const setManager = useMutation({
    mutationFn: ({ siteId, employeeId }: { siteId: string; employeeId: string | null }) =>
      api.patch<{
        id: string;
        managerEmployeeId: string | null;
        managerName: string | null;
        managerEmail: string | null;
      }>(`/api/inv-sites/${siteId}/manager`, { employeeId }),
    onSuccess: (result) => {
      queryClient.setQueryData<InvSitesPayload>(['inv-sites'], (old) => {
        if (!old) return old;
        return {
          ...old,
          sites: old.sites.map((s) =>
            s.id === result.id
              ? {
                  ...s,
                  managerEmployeeId: result.managerEmployeeId,
                  managerName: result.managerName,
                  managerEmail: result.managerEmail,
                }
              : s,
          ),
        };
      });
      toast('Gestionnaire mis à jour', 'success');
    },
    onError: () => toast('Erreur lors de la mise à jour du gestionnaire', 'error'),
  });

  const actifs = sites.filter((s) => s.isActive);
  const inactifs = sites.filter((s) => !s.isActive);

  if (isLoading)
    return (
      <div className="p-6 space-y-4">
        <PageHeader title="Sites Inventaire" description="Succursales SOFI disponibles dans GS-Inventaire" />
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      </div>
    );

  if (isError)
    return (
      <div className="p-6">
        <PageHeader title="Sites Inventaire" description="Erreur de chargement" />
        <button type="button" onClick={() => void refetch()} className="mt-4 text-sm text-brand-600 underline">
          Réessayer
        </button>
      </div>
    );

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Sites Inventaire"
        description={`${actifs.length} site${actifs.length > 1 ? 's' : ''} actif${actifs.length > 1 ? 's' : ''} sur ${sites.length} succursales SOFI`}
        onRefresh={() => void refetch()}
        refreshLoading={isFetching}
      />

      <div className="space-y-2">
        <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-1">
          Actifs dans GS-Inventaire — {actifs.length}
        </p>
        {actifs.map((site) => (
          <SiteRow
            key={site.id}
            site={site}
            employees={employees}
            onToggle={() => toggle.mutate(site.id)}
            onSetManager={(employeeId) =>
              setManager.mutate({ siteId: site.id, employeeId })
            }
            isPending={toggle.isPending || setManager.isPending}
          />
        ))}
      </div>

      {inactifs.length > 0 && (
        <div className="space-y-2">
          <p className="text-meta font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 px-1">
            Masqués — {inactifs.length}
          </p>
          {inactifs.map((site) => (
            <SiteRow
              key={site.id}
              site={site}
              employees={employees}
              onToggle={() => toggle.mutate(site.id)}
              onSetManager={(employeeId) =>
                setManager.mutate({ siteId: site.id, employeeId })
              }
              isPending={toggle.isPending || setManager.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SiteRow({
  site,
  employees,
  onToggle,
  onSetManager,
  isPending,
}: {
  site: InvSite;
  employees: Employee[];
  onToggle: () => void;
  onSetManager: (employeeId: string | null) => void;
  isPending: boolean;
}) {
  const siteEmployees = employees.filter((e) => e.succursale_id === site.succursaleId);
  const hasManager = site.managerEmployeeId !== null;

  return (
    <div
      className={`rounded-xl border px-4 py-3 transition-colors ${
        site.isActive
          ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
          : 'bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800 opacity-60'
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            site.isActive ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-slate-100 dark:bg-slate-800'
          }`}
        >
          <Building2 size={16} className={site.isActive ? 'text-blue-500' : 'text-slate-400'} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-field font-semibold text-slate-800 dark:text-slate-100">{site.name}</span>
            <span className="text-meta font-mono text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded-sm">
              {site.codeSofi}
            </span>
          </div>
          {site.nomSofi && site.nomSofi !== site.name && (
            <p className="text-meta text-slate-400 dark:text-slate-500 mt-0.5 truncate">
              {site.nomSofi}
              {site.ville ? ` · ${site.ville}` : ''}
            </p>
          )}
          {site.ville && (site.nomSofi === site.name || !site.nomSofi) && (
            <p className="text-meta text-slate-400 dark:text-slate-500 mt-0.5">{site.ville}</p>
          )}
        </div>

        <button
          type="button"
          onClick={onToggle}
          disabled={isPending}
          className="shrink-0 flex items-center gap-1.5 text-sm font-medium transition-colors touch-manipulation"
          title={site.isActive ? 'Désactiver ce site' : 'Activer ce site'}
        >
          {site.isActive ? (
            <>
              <ToggleRight size={24} className="text-emerald-500" />
              <span className="hidden sm:inline text-emerald-600 dark:text-emerald-400 text-meta">Actif</span>
            </>
          ) : (
            <>
              <ToggleLeft size={24} className="text-slate-300 dark:text-slate-600" />
              <span className="hidden sm:inline text-slate-400 text-meta">Inactif</span>
            </>
          )}
        </button>
      </div>

      <div className="flex items-center gap-3 mt-2 pl-14">
        <User size={12} className="text-slate-400 shrink-0" />
        <select
          value={site.managerEmployeeId ?? ''}
          onChange={(e) => onSetManager(e.target.value || null)}
          disabled={isPending || siteEmployees.length === 0}
          className="text-meta text-slate-600 dark:text-slate-300 bg-transparent border-0 border-b border-dashed border-slate-200 dark:border-slate-600 focus:outline-hidden focus:border-brand-400 cursor-pointer pr-4 py-0.5 min-w-0 max-w-56"
        >
          <option value="">
            {siteEmployees.length === 0 ? 'Aucun employé SOFI' : '— Choisir un gestionnaire —'}
          </option>
          {siteEmployees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name}
            </option>
          ))}
        </select>

        {/* Gestionnaire affecte mais courriel absent du referentiel : on le DIT.
            Avant la mig 172 ce cas etait masque par la sentinelle admin@ — l'ecran
            affichait une adresse joignable au lieu d'un trou visible. */}
        {hasManager && (
          <span className="text-meta text-slate-400 dark:text-slate-500 truncate">
            {site.managerEmail ?? 'Courriel non renseigné'}
          </span>
        )}
      </div>
    </div>
  );
}
